import React, { useState } from 'react';
import { TEMPLATES, TemplateSVG } from '../data/templates';
import { MAX_COVER_LETTERS_PER_PROJECT } from '../data/projectStore';
import Header from './Header';
import './ProjectHome.css';

const PENCIL_SVG = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const RENAME_SVG = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
    <line x1="15" y1="5" x2="19" y2="9"/>
  </svg>
);

// ── Project name heading ──────────────────────────────────
function EditableName({ name, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(name);

  const commit = () => {
    setEditing(false);
    const trimmed = val.trim();
    if (trimmed && trimmed !== name) onSave(trimmed);
    else setVal(name);
  };

  if (editing) {
    return (
      <input
        className="proj-home-name-input"
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter')  commit();
          if (e.key === 'Escape') { setVal(name); setEditing(false); }
        }}
        autoFocus
      />
    );
  }

  return (
    <h1 className="proj-home-name" onClick={() => setEditing(true)} title="Click to rename">
      {name}
      <span className="proj-home-name-pencil">{RENAME_SVG}</span>
    </h1>
  );
}

// ── Resume doc card ───────────────────────────────────────
function ResumeCard({ resume, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const template = TEMPLATES.find(t => t.id === resume?.templateId);

  if (!resume) {
    return (
      <div className="proj-doc-card resume-card empty">
        <div className="proj-doc-card-header">
          <span className="proj-doc-card-label">Resume</span>
        </div>
        <div className="proj-doc-empty-state">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          <p>No resume yet</p>
          <button className="btn-proj-doc-create" onClick={onEdit}>Create Resume</button>
        </div>
      </div>
    );
  }

  return (
    <div className="proj-doc-card resume-card">
      <div className="proj-doc-card-header">
        <span className="proj-doc-card-label">Resume</span>
        <span className="proj-doc-template-badge">{resume.templateName}</span>
      </div>

      {template && (
        <div className="proj-doc-thumbnail proj-doc-thumbnail-clickable" onClick={onEdit}>
          <TemplateSVG template={template} />
          <div className="proj-doc-thumbnail-overlay">
            {PENCIL_SVG}
            Edit Resume
          </div>
        </div>
      )}

      <div className="proj-doc-info">
        <div className="proj-doc-person-name">
          {resume.data.personal.name || 'Resume'}
        </div>
        {resume.data.personal.title && (
          <div className="proj-doc-person-title">{resume.data.personal.title}</div>
        )}
        <div className="proj-doc-counts">
          <div className="proj-doc-counts-text">
            {resume.data.experience.length > 0 && (
              <span>{resume.data.experience.length} experience {resume.data.experience.length === 1 ? 'entry' : 'entries'}</span>
            )}
            {resume.data.skills.length > 0 && (
              <span>{resume.data.skills.length} skills</span>
            )}
          </div>
          {confirmDelete ? (
            <div className="proj-doc-delete-confirm">
              <span>Delete?</span>
              <button className="btn-proj-confirm-yes" onClick={() => { setConfirmDelete(false); onDelete(); }}>Yes</button>
              <button className="btn-proj-confirm-no"  onClick={() => setConfirmDelete(false)}>No</button>
            </div>
          ) : (
            <button className="btn-proj-delete-resume" onClick={() => setConfirmDelete(true)} title="Delete resume">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Cover letters card ────────────────────────────────────
function CoverLettersCard({ project, onAddCoverLetter, onEditCoverLetter, onDeleteCoverLetter, onLinkCoverLetter, onUnlinkCoverLetter }) {
  const [confirmId, setConfirmId] = useState(null);
  const cls = project.coverLetters;
  const atLimit = cls.length >= MAX_COVER_LETTERS_PER_PROJECT;
  const hasResume = !!project.resume;

  return (
    <div className="proj-doc-card cl-card">
      <div className="proj-doc-card-header">
        <span className="proj-doc-card-label">Cover Letters</span>
        <span className="proj-doc-cl-count">{cls.length} / {MAX_COVER_LETTERS_PER_PROJECT}</span>
      </div>

      {cls.length === 0 ? (
        <div className="proj-doc-empty-state">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" opacity="0.3">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          <p>No cover letters yet</p>
          {!hasResume && (
            <p className="proj-doc-tip">💡 We recommend adding a resume first so the AI has more to work with.</p>
          )}
          <button className="btn-proj-doc-create" onClick={onAddCoverLetter}>
            Create Cover Letter
          </button>
        </div>
      ) : (
        <div className="proj-cl-list">
          {cls.map(cl => (
            <div
              key={cl.id}
              className="proj-cl-item"
              onClick={() => confirmId !== cl.id && onEditCoverLetter(cl.id)}
              style={{ cursor: 'pointer' }}
            >
              <div className="proj-cl-item-info">
                <div className="proj-cl-company">{cl.targetCompany || `Cover Letter ${cls.indexOf(cl) + 1}`}</div>
                {cl.targetRole && <div className="proj-cl-role">{cl.targetRole}</div>}
              </div>

              <div className="proj-cl-item-badges">
                {cl.linkedToResume && (
                  <span className="proj-cl-linked-badge" title="Linked to resume">🔗 Linked</span>
                )}
                <span className="proj-cl-template-badge">{cl.templateName}</span>
              </div>

              {confirmId === cl.id ? (
                <div className="proj-doc-delete-confirm" onClick={e => e.stopPropagation()}>
                  <span>Delete?</span>
                  <button className="btn-proj-confirm-yes" onClick={() => { setConfirmId(null); onDeleteCoverLetter(cl.id); }}>Yes</button>
                  <button className="btn-proj-confirm-no"  onClick={() => setConfirmId(null)}>No</button>
                </div>
              ) : (
                <div className="proj-cl-item-actions" onClick={e => e.stopPropagation()}>
                  {!hasResume && !cl.linkedToResume && onLinkCoverLetter && (
                    <button className="btn-proj-cl-edit-icon" onClick={() => onLinkCoverLetter(cl.id)} title="Link to a resume">
                      🔗 Link to Resume
                    </button>
                  )}
                  {cl.linkedToResume && onUnlinkCoverLetter && (
                    <button className="btn-proj-cl-unlink" onClick={() => onUnlinkCoverLetter(cl.id)} title="Unlink from resume">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        <line x1="2" y1="2" x2="22" y2="22" stroke="currentColor"/>
                      </svg>
                      Unlink
                    </button>
                  )}
                  <button
                    className="btn-proj-cl-delete"
                    onClick={() => setConfirmId(cl.id)}
                    title="Delete cover letter"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          ))}
          {!atLimit && (
            <button className="btn-proj-cl-add" onClick={onAddCoverLetter}>
              + Add Another Cover Letter
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── ProjectHome ───────────────────────────────────────────
export default function ProjectHome({
  project,
  onBackToProjects,
  onEditResume,
  onChangeResumeTemplate,
  onCompareResumeTemplate,
  onUpdateName,
  onDeleteResume,
  onAddCoverLetter,
  onEditCoverLetter,
  onDeleteCoverLetter,
  onLinkCoverLetter,
  onUnlinkCoverLetter,
  user,
  lastSaved, isSaving, saveFailed,
}) {
  return (
    <div className="proj-home">
      <Header onHome={onBackToProjects} user={user} lastSaved={lastSaved} isSaving={isSaving} saveFailed={saveFailed} />

      <main className="proj-home-main">
        <button className="proj-home-back" onClick={onBackToProjects}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
          Home
        </button>

        <EditableName name={project.name} onSave={onUpdateName} />

        <div className="proj-home-docs">
          <ResumeCard
            resume={project.resume}
            onEdit={onEditResume}
            onDelete={onDeleteResume}
          />
          <CoverLettersCard
            project={project}
            onAddCoverLetter={onAddCoverLetter}
            onEditCoverLetter={onEditCoverLetter}
            onDeleteCoverLetter={onDeleteCoverLetter}
            onLinkCoverLetter={onLinkCoverLetter}
            onUnlinkCoverLetter={onUnlinkCoverLetter}
          />
        </div>
      </main>
    </div>
  );
}
